import React, { useCallback, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Button,
  Color,
  Display,
  FontFamily,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { basketSlotChange, basketIsShowDetailsViewFirstChange } from 'actions/basket'
import { boxSummaryVisibilityChange, trackDeliveryOptionsShown } from 'actions/boxSummary'
import config from 'config/basket'
import { checkoutBasket } from 'routes/Menu/actions/menuCheckoutClick'
import { CheckoutCounter } from 'routes/Menu/components/BoxSummary/Banner/CheckoutCounter/CheckoutCounter'
import { useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { getBasketPostcode, getBasketSlotId, getBasketUsageCount } from 'selectors/basket'
import {
  getDeliveryDetailsInstructions,
  getDeliveryDetailsInstructionsCustom,
} from 'selectors/deliveryDetails'
import { createGetActionTypeIsPending, createGetErrorForActionType } from 'selectors/status'

import css from './CheckoutButton.css'

type Props = {
  view: string
  section: string
  hideCounter?: boolean
  isFullWidth?: boolean
  isBasketComprehensionEnabled?: boolean
}

const useCheckoutButtonIsPending = () => {
  const orderSaveError = useSelector(createGetErrorForActionType(actionTypes.ORDER_SAVE))

  const { isPending: pricingPending } = usePricing()

  const checkoutPending = useSelector(createGetActionTypeIsPending(actionTypes.BASKET_CHECKOUT))
  const basketPreviewOrderChangePending = useSelector(
    createGetActionTypeIsPending('BASKET_PREVIEW_ORDER_CHANGE'),
  )
  const orderSavePending = useSelector(createGetActionTypeIsPending('ORDER_SAVE'))
  const loadingOrderPending = useSelector(createGetActionTypeIsPending(actionTypes.LOADING_ORDER))
  const menuFetchData = useSelector(createGetActionTypeIsPending(actionTypes.MENU_FETCH_DATA))

  const isPending =
    (checkoutPending ||
      pricingPending ||
      basketPreviewOrderChangePending ||
      orderSavePending ||
      loadingOrderPending ||
      menuFetchData) &&
    orderSaveError === null

  return isPending
}

const BasketComprehensionButton = withCitrusTheme(Button, (theme) => ({
  '&:disabled, &:disabled:hover': {
    backgroundColor: theme.color(Color.ColdGrey_700),
  },
  '&:disabled span': {
    color: theme.color(Color.ColdGrey_100),
  },
}))

export const CheckoutButton = ({
  view,
  section,
  hideCounter = false,
  isFullWidth = false,
  isBasketComprehensionEnabled = false,
}: Props) => {
  const numRecipesOverBasketLimit = useNumRecipesOverBasketLimit()
  const isPending = useCheckoutButtonIsPending()

  const orderSaveError = useSelector(createGetErrorForActionType(actionTypes.ORDER_SAVE))

  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const hasBasketPostCode = useSelector(getBasketPostcode)
  const deliveryInstructions = useSelector(getDeliveryDetailsInstructions)
  const deliveryInstructionsCustom = useSelector(getDeliveryDetailsInstructionsCustom)
  const slotId = useSelector(getBasketSlotId)

  const hasDeliveryInstructions = !!deliveryInstructions || !!deliveryInstructionsCustom
  const isNumRecipesOverBasketLimit = !!numRecipesOverBasketLimit

  const hasProspectNotSeenWizardInvalid =
    hasProspectNotSeenWizard && (!hasBasketPostCode || !hasDeliveryInstructions)

  const numRecipes = useSelector(getBasketUsageCount)

  const isDisabled =
    isPending ||
    numRecipes < config.minRecipesNum ||
    orderSaveError !== null ||
    isNumRecipesOverBasketLimit ||
    !slotId

  const { pricing } = usePricing()
  const dispatch = useDispatch()

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation()
      if (hasProspectNotSeenWizardInvalid) {
        dispatch(basketSlotChange())
        dispatch(basketIsShowDetailsViewFirstChange(false))
        dispatch(boxSummaryVisibilityChange(true))
        dispatch(trackDeliveryOptionsShown())
      } else {
        datadogRum.addAction('basket_checkout_button_clicked', {
          slotId,
        })
        dispatch(checkoutBasket({ section, view, pricing }))
      }
    },
    [dispatch, section, view, pricing, hasProspectNotSeenWizardInvalid, slotId],
  )

  // CheckoutCounter should change background color when the button is hovered.
  // While a css-only solution is possible in theory (`.button:hover
  // .counter`), we cannot refer to class names defined in other css module
  // files, or via CSS-in-JS.
  const [isButtonHovered, setIsButtonHovered] = useState(false)
  const CheckoutButtonComponent = isBasketComprehensionEnabled ? BasketComprehensionButton : Button

  return (
    <div
      className={classNames(css.buttonContainer, {
        [css.isFullWidth]: isFullWidth,
      })}
      data-testing="boxSummaryButton"
      onMouseEnter={() => setIsButtonHovered(true)}
      onMouseLeave={() => setIsButtonHovered(false)}
    >
      <CheckoutButtonComponent
        data-testid="checkout-button"
        height={48}
        onClick={handleClick}
        disabled={isDisabled}
        width={isFullWidth ? '100%' : undefined}
      >
        {isBasketComprehensionEnabled ? (
          <Text color={Color.White} display={Display.Inline} fontFamily={FontFamily.Bold}>
            Checkout
          </Text>
        ) : (
          <span className={css.checkoutLabel}>Checkout</span>
        )}
        {hideCounter ? null : (
          <CheckoutCounter
            isDisabled={isDisabled}
            isButtonHovered={isButtonHovered}
            numRecipes={numRecipes}
          />
        )}
      </CheckoutButtonComponent>
    </div>
  )
}
